// MediaLightbox.js — Responsive
import React from "react";
import { createPortal } from "react-dom";

export default function MediaLightbox({ media, onClose }) {
  // ── Zoom / pan state — lives on the media itself, never the page ──────
  const [scale, setScale] = React.useState(1);
  const [tx, setTx] = React.useState(0);
  const [ty, setTy] = React.useState(0);

  const draggingRef = React.useRef(false);
  const lastPointRef = React.useRef({ x: 0, y: 0 });
  const pinchPointsRef = React.useRef(new Map()); // pointerId -> {x,y}
  const pinchStartDistRef = React.useRef(0);
  const pinchStartScaleRef = React.useRef(1);
  const stageRef = React.useRef(null);
  const lastTapRef = React.useRef(0);

  const MIN_SCALE = 1;
  const MAX_SCALE = 4;

  const resetZoom = React.useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  // Reset zoom whenever a new media item opens
  React.useEffect(() => {
    resetZoom();
  }, [media, resetZoom]);

  // Close on Escape key
  React.useEffect(() => {
    if (!media) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [media, onClose]);

  // Lock background scroll
  React.useEffect(() => {
    if (!media) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [media]);

  const clampScale = (s) =>
    Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

  // ── Desktop wheel zoom ────────────────────────────────────────────────
  const handleWheel = (e) => {
    try {
      e.preventDefault();
    } catch (_) {}

    e.stopPropagation();

    const delta = -e.deltaY * 0.0025;

    setScale((s) => {
      const next = clampScale(s + delta);

      if (next === MIN_SCALE) {
        setTx(0);
        setTy(0);
      }

      return next;
    });
  };

  // ── Double-tap / double-click zoom ────────────────────────────────────
  const handleDoubleTapOrClick = () => {
    setScale((s) => {
      if (s > MIN_SCALE) {
        setTx(0);
        setTy(0);
        return MIN_SCALE;
      }

      return 2;
    });
  };

  // ── Pointer gestures ──────────────────────────────────────────────────
  const handlePointerDown = (e) => {
    e.stopPropagation();

    pinchPointsRef.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
    });

    if (pinchPointsRef.current.size === 2) {
      const [p1, p2] = [...pinchPointsRef.current.values()];

      pinchStartDistRef.current = Math.hypot(
        p2.x - p1.x,
        p2.y - p1.y
      );

      pinchStartScaleRef.current = scale;
    } else if (pinchPointsRef.current.size === 1) {
      const now = Date.now();

      if (now - lastTapRef.current < 280) {
        handleDoubleTapOrClick();
      }

      lastTapRef.current = now;

      if (scale > MIN_SCALE) {
        draggingRef.current = true;
        lastPointRef.current = {
          x: e.clientX,
          y: e.clientY,
        };
      }
    }
  };

  const handlePointerMove = (e) => {
    if (!pinchPointsRef.current.has(e.pointerId)) return;

    e.stopPropagation();

    pinchPointsRef.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
    });

    if (pinchPointsRef.current.size === 2) {
      const [p1, p2] = [...pinchPointsRef.current.values()];

      const dist = Math.hypot(
        p2.x - p1.x,
        p2.y - p1.y
      );

      if (pinchStartDistRef.current > 0) {
        setScale(
          clampScale(
            pinchStartScaleRef.current *
              (dist / pinchStartDistRef.current)
          )
        );
      }
    } else if (draggingRef.current && scale > MIN_SCALE) {
      const dx = e.clientX - lastPointRef.current.x;
      const dy = e.clientY - lastPointRef.current.y;

      lastPointRef.current = {
        x: e.clientX,
        y: e.clientY,
      };

      setTx((v) => v + dx);
      setTy((v) => v + dy);
    }
  };

  const handlePointerUp = (e) => {
    pinchPointsRef.current.delete(e.pointerId);

    if (pinchPointsRef.current.size < 2) {
      pinchStartDistRef.current = 0;
    }

    if (pinchPointsRef.current.size === 0) {
      draggingRef.current = false;
    }

    if (scale <= MIN_SCALE) {
      setTx(0);
      setTy(0);
    }
  };

  if (!media) return null;
  if (typeof document === "undefined") return null;

  const type = media.type || (media.mimeType || "").split("/")[0];

  // Videos now use the same zoom gestures as images.
  const isZoomable =
    type === "image" ||
    type === "gif" ||
    type === "video";

  const sharedMediaStyle = {
    maxHeight:
      "calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 64px)",
    transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
    transformOrigin: "center center",
    transition: draggingRef.current
      ? "none"
      : "transform 0.15s ease-out",
    cursor: scale > MIN_SCALE ? "grab" : "zoom-in",
    touchAction: "none",
  };

  const content = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={onClose}
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        backgroundColor: "#000",
        touchAction: "none",
        overscrollBehavior: "contain",
        margin: 0,
      }}
    >
      <div
        ref={stageRef}
        className="relative w-full h-full flex items-center justify-center overflow-hidden p-4"
        onClick={(e) => e.stopPropagation()}
        onWheel={isZoomable ? handleWheel : undefined}
        onPointerDown={isZoomable ? handlePointerDown : undefined}
        onPointerMove={isZoomable ? handlePointerMove : undefined}
        onPointerUp={isZoomable ? handlePointerUp : undefined}
        onPointerCancel={isZoomable ? handlePointerUp : undefined}
        style={{ touchAction: "none" }}
      >
        {(type === "image" || type === "gif") && (
          <img
            src={media.url}
            alt=""
            draggable={false}
            className="max-w-full max-h-full object-contain rounded-lg select-none"
            style={sharedMediaStyle}
          />
        )}

        {type === "video" && (
          <video
            src={media.url}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-full rounded-lg"
            style={sharedMediaStyle}
          />
        )}

        {type === "audio" && (
          <div className="w-full max-w-sm bg-neutral-900 rounded-2xl p-6">
            <div className="text-4xl text-center mb-4">🎵</div>

            <audio
              src={media.url}
              controls
              autoPlay
              className="w-full"
            />
          </div>
        )}

        {type !== "image" &&
          type !== "gif" &&
          type !== "video" &&
          type !== "audio" && (
            <iframe
              src={media.url}
              className="w-full h-full rounded-lg bg-white"
              style={{
                maxWidth: "900px",
                maxHeight:
                  "calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 80px)",
              }}
              title="document"
            />
          )}

        {/* Close button */}
        <button
          className="
            absolute top-2 right-2
            w-9 h-9 rounded-full
            bg-black/50 hover:bg-black/70
            text-white flex items-center justify-center
            text-lg touch-manipulation
          "
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}